import React, { useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Flex,
  Spinner,
  Center,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { ConfirmDialog } from 'src/components/molecules/ConfirmDialog';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { KPIConfiguration } from 'src/components/molecules/KPIConfiguration';
import { ModelTypeRadio } from 'src/components/molecules/ModelTypeRadio';
import { useModelForm } from '../../hooks/useModelForm';
import { MediaSelectList } from '../molecules/MediaSelectList';
import { CampaignSelectList } from '../molecules/CampaignSelectList';
import { LtParentSelect } from '../molecules/LtParentSelect';

type EditModelPageProps = {
  modelId: string;
  onComplete: () => void;
};

export const EditModelPage: React.FC<EditModelPageProps> = ({
  modelId,
  onComplete,
}) => {
  const {
    models,
    kpiSources,
    kpiMetrics,
    mediaPlatforms,
    campaignsWithGroups,
    eligibleLtParents,
    isLoading,
    isSubmitting,
    areAllModelsValid,
    toastState,
    hideToast,
    handleModelNameChange,
    handleModelTypeChange,
    handleParentModelChange,
    handleSourceChange,
    handleSourceReset,
    handleMetricChange,
    handleMetricReset,
    handleUnitChange,
    handleUnitReset,
    handleMediaChange,
    handleCampaignSelectionsChange,
    handleSubmit,
    handleCancel,
  } = useModelForm({ mode: 'edit', modelId, onComplete });

  const [isRerunConfirmOpen, setIsRerunConfirmOpen] = useState(false);

  const model = models[0];

  if (isLoading && !model?.modelName) {
    return (
      <Center py={20}>
        <Spinner color="icon.green" size="lg" />
      </Center>
    );
  }

  if (!model) {
    return (
      <Center py={20}>
        <Text color="content.muted">Model not found</Text>
      </Center>
    );
  }

  return (
    <>
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />
      <Box
        bg="surface.panel"
        borderRadius="16px"
        border="1px solid"
        borderColor="surface.border"
        w="100%"
        p={6}>
        {/* Page Title */}
        <Text fontSize="16px" fontWeight={600} color="content.title" mb={6}>
          EDIT YOUR MODEL
        </Text>

        <VStack spacing={6} align="stretch">
          {/* Model Name */}
          <Box>
            <FormControl>
              <FormLabel
                fontSize="16px"
                fontWeight={500}
                color="content.title"
                mb={3}>
                Model name
              </FormLabel>
              <InputGroup>
                <Input
                  placeholder="Name your model"
                  value={model.modelName}
                  onChange={(e) =>
                    handleModelNameChange(model.id, e.target.value)
                  }
                  bg="surface.panel"
                  border="1px solid"
                  borderColor="surface.border"
                  borderRadius="md"
                  py={3}
                  px={4}
                  pr={10}
                  fontSize="16px"
                  _placeholder={{ color: 'content.muted' }}
                  _focus={{
                    borderColor: 'icon.green',
                    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                  }}
                />
                <InputRightElement h="100%">
                  <Icon iconName="pencil" boxSize={4} color="content.muted" />
                </InputRightElement>
              </InputGroup>
            </FormControl>
          </Box>

          {/* The model type is fixed at creation (backend rejects changes):
              switching types mid-life orphans LT parent references or breaks
              LT children built on an MM parent. */}
          <ModelTypeRadio
            value={model.modelType}
            onChange={(next) => handleModelTypeChange(model.id, next)}
            isDisabled
          />

          {model.modelType === 'lt' ? (
            <LtParentSelect
              eligibleParents={eligibleLtParents}
              parentModelId={model.parentModelId}
              onChange={(parentId) =>
                handleParentModelChange(model.id, parentId)
              }
              isDisabled={isLoading}
            />
          ) : (
            <>
              <KPIConfiguration
                model={model}
                onSourceChange={handleSourceChange}
                onSourceReset={handleSourceReset}
                onMetricChange={handleMetricChange}
                onMetricReset={handleMetricReset}
                onUnitChange={handleUnitChange}
                onUnitReset={handleUnitReset}
                kpiSources={kpiSources}
                kpiMetrics={kpiMetrics}
                isLoading={isLoading}
              />

              <MediaSelectList
                mediaPlatforms={mediaPlatforms}
                selectedMedia={model.selectedMedia}
                onChange={(selectedMedia) =>
                  handleMediaChange(model.id, selectedMedia)
                }
                isDisabled={isLoading}
              />

              <CampaignSelectList
                campaigns={campaignsWithGroups}
                selectedCampaigns={model.campaignSelections}
                onChange={(selections) =>
                  handleCampaignSelectionsChange(model.id, selections)
                }
                isDisabled={isLoading || model.selectedMedia.length === 0}
                emptyMessage={
                  model.selectedMedia.length === 0
                    ? 'Select media above to load campaigns'
                    : 'No campaigns available'
                }
              />
            </>
          )}
        </VStack>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify={{ base: 'stretch', md: 'flex-end' }}
          alignItems="center"
          gap={4}
          mt={8}>
          <Button
            buttonName="back-edit-model"
            variant="outline"
            borderColor="gray.300"
            color="content.body"
            bg="surface.panel"
            borderRadius="lg"
            w={{ base: '100%', md: '160px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleCancel}
            _hover={{ bg: 'sidebar.itemHoverBg' }}>
            Back
          </Button>

          <Button
            buttonName="save-edit-model"
            bg="icon.green"
            color="white"
            borderRadius="lg"
            w={{ base: '100%', md: '200px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={() => setIsRerunConfirmOpen(true)}
            isLoading={isSubmitting}
            loadingText="Re-running..."
            isDisabled={!areAllModelsValid || isSubmitting}
            _hover={{ opacity: 0.9 }}
            _disabled={{
              bg: 'icon.green',
              cursor: 'not-allowed',
              opacity: 0.5,
            }}>
            Re-run model
          </Button>
        </Flex>
      </Box>

      <ConfirmDialog
        isOpen={isRerunConfirmOpen}
        onClose={() => setIsRerunConfirmOpen(false)}
        onConfirm={() => {
          setIsRerunConfirmOpen(false);
          handleSubmit();
        }}
        headerText="Re-run model?"
        bodyText="Editing this model re-runs it from scratch: all forecasts and goals you have created on it will be deleted, and its results will be replaced once the new run completes. Continue?"
        confirmText="Re-run model"
      />
    </>
  );
};
